import {
  ColumnFactory,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlags,
  FeatureFlagsContext,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { DashboardsDisplayGridRowDataFragment } from '../../../gen-src/queries/DashboardsDisplayGridRowDataFragment';
import { DashboardOrderField } from '../../../gen-src/queries/globalTypes';
import { createLinkCellFormatter, createLinkOrTextCellFormatter } from '../datagrid/cellFormatters/LinkCellFormatter';
import { createObfuscatedLinkOrTextCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { DashboardsValidSortFields } from '../sorting/sortProviders/DashboardsGridSortProvider';

const shouldBeObfuscated: (rowData: DashboardsDisplayGridRowData) => boolean = ({ name }) => name === null;

export interface DashboardsDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
  readonly path: string | null;
  readonly workbookName: string | null;
  readonly workbookVizportalUrlId: string | null;
  readonly projectName: string | null;
  readonly projectVizportalUrlId: string | null;
  readonly ownerName: string | null;
  readonly ownerUsername: string | null;
  readonly ownerDomain: string | null;
}

export function getRowDataForDashboard(dashboard: DashboardsDisplayGridRowDataFragment): DashboardsDisplayGridRowData {
  return {
    id: dashboard.id,
    name: dashboard.name,
    path: dashboard.path,
    workbookName: dashboard.workbook ? dashboard.workbook.name : null,
    workbookVizportalUrlId: dashboard.workbook ? dashboard.workbook.vizportalUrlId : null,
    projectName: dashboard.workbook ? dashboard.workbook.projectName : null,
    projectVizportalUrlId: dashboard.workbook ? dashboard.workbook.projectVizportalUrlId : null,
    ownerName: dashboard.workbook ? dashboard.workbook.owner.name : null,
    ownerUsername: dashboard.workbook ? dashboard.workbook.owner.username : null,
    ownerDomain: dashboard.workbook ? dashboard.workbook.owner.domain : null,
  };
}

type DashboardsColumnFactory = ColumnFactory<
  GridSortProps<DashboardsValidSortFields>,
  DashboardsDisplayGridRowData,
  DashboardsValidSortFields
>;

function columns(featureFlags: FeatureFlags): ReadonlyArray<DashboardsColumnFactory> {
  const cols: ReadonlyArray<DashboardsColumnFactory> = [
    (sortParams) => ({
      name: 'name',
      dataKey: DashboardOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkOrTextCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { path }) => path ? urls.viewPath(path) : null,
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'workbook',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderWorkbook(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkOrTextCellFormatter(
        ({ workbookName }) => workbookName === null,
        ({ workbookName }) => workbookName,
        (urls, { workbookVizportalUrlId }) => workbookVizportalUrlId ? urls.workbookLineagePath(workbookVizportalUrlId) : null
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: featureFlags.personalSpaceEnabled ? 'location' : 'project',
      dataKey: UnsortableColumnKey,
      headerText: featureFlags.personalSpaceEnabled ? Messages.GridHeaderLocation() : Messages.GridHeaderProject(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkOrTextCellFormatter(
        ({ projectName }) => projectName || '',
        (urls, { projectVizportalUrlId }) => projectVizportalUrlId ? urls.projectPath(projectVizportalUrlId) : null
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.Owner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkCellFormatter(
        // If ownerName, ownerUsername, and ownerDomain are null, there is likely a permission denied issue
        ({ ownerName, ownerUsername }) => ownerName || ownerUsername || '',
        (urls, { ownerUsername, ownerDomain }) => {
          if (ownerUsername && ownerDomain) {
            return urls.userPath(ownerUsername, ownerDomain);
          } else {
            return '';
          }
        }
      ),
      resizable: true,
      sortable: false,
    })
  ];

  return cols.filter(Boolean) as ReadonlyArray<DashboardsColumnFactory>;
}

export type DashboardsDisplayGridProps =
  UsualDisplayGridProps<DashboardsDisplayGridRowData, GraphQLError> &
  GridSortProps<DashboardsValidSortFields>;

export function DashboardsDisplayGrid(props: DashboardsDisplayGridProps): JSX.Element {
  const featureFlags = React.useContext(FeatureFlagsContext);

  const cols = columns(featureFlags).map(columnFactory => columnFactory(props));
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
